import {
  AssignmentResponseData,
  PayrollListSalaryComponentsQueryVariables,
} from 'api/gql-utils/salaryComponents/types';
import {
  deductionTypeFiltersMap,
  fnfSalaryComponentTypes,
  payTypeFiltersMap,
  statusFiltersMap,
  sectionToCategoryMap,
} from './constants';
import {
  ComponentType,
  DeductionsComponentType,
  EarningsComponentType,
  SalaryComponent,
} from './types';
import { OTSalaryComponentListItem } from 'api/gql-utils/overtime/types';

export const getTaxRegimeText = (
  underOldRegime: boolean = false,
  underNewRegime: boolean = false,
) => {
  if (underOldRegime && underNewRegime) {
    return 'Under Old & New Regime';
  } else if (underOldRegime) {
    return 'Under Old Regime';
  } else if (underNewRegime) {
    return 'Under New Regime';
  } else {
    return '';
  }
};

export const getTableItemCountLimits = (
  totalNumberOfItems: number,
  currentPage: number,
  itemsPerPage: number,
  componentType = '',
) => {
  if (totalNumberOfItems === 0) {
    return 'No items to display';
  }

  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * itemsPerPage, totalNumberOfItems);

  return `Showing ${startItem}-${endItem} ${componentType} components`;
};

export type SectionName = keyof typeof sectionToCategoryMap;

export const getQueryVariables = (
  section: SectionName,
  currentFilters: Record<string, string>,
  searchQuery?: string,
): PayrollListSalaryComponentsQueryVariables => {
  const category = sectionToCategoryMap[section];

  const taxabilityFilter =
    currentFilters['taxability[]'] && currentFilters['taxability[]'].length > 0
      ? {
          taxExemptInOldRegime: currentFilters['taxability[]'].includes('old-regime'),
          taxExemptInNewRegime: currentFilters['taxability[]'].includes('new-regime'),
        }
      : undefined;

  // Determine payType based on section and filters
  let payType: any;

  if (category === 'EARNINGS_CATEGORY') {
    if (section === 'npb') {
      // For non-payable benefits, always filter by non_payable_benefit payType
      payType = payTypeFiltersMap['non_payable_benefit'];
    } else if (currentFilters.payType) {
      // For regular earnings, use the filter if provided
      payType = payTypeFiltersMap[currentFilters.payType];
    }
  } else if (category === 'DEDUCTIONS_CATEGORY' && currentFilters.deduction_type) {
    payType = deductionTypeFiltersMap[currentFilters.deduction_type];
  }

  return {
    category,
    status: statusFiltersMap[currentFilters.status] || statusFiltersMap.all,
    payType,
    taxability: taxabilityFilter,
    searchQuery: searchQuery || undefined,
  };
};

export const filterOutFnfComponents = <T extends SalaryComponent>(
  components: AssignmentResponseData<T>[],
) => {
  return components.filter((addition) => {
    const salaryComponent = addition?.componentDetails;
    if (fnfSalaryComponentTypes?.includes(salaryComponent?.componentType as ComponentType)) {
      return false;
    }

    return true;
  });
};

export const filterOutRecurringDeductions = (
  components: AssignmentResponseData<DeductionsComponentType>[],
) => {
  return components.filter((deduction) => {
    const salaryComponent = deduction.componentDetails;
    if (salaryComponent.settings.deductionDetails.deductionType === 'RECURRING') {
      return false;
    }
    return true;
  });
};

/**
 * Converts a string to title case
 * Handles snake_case, camelCase, PascalCase, and kebab-case strings
 *
 * @param str - The string to convert
 * @returns The string in title case
 *
 * @example
 * toTitleCase('fixed_amount') // 'Fixed Amount'
 * toTitleCase('percentage_of_ctc') // 'Percentage Of Ctc'
 * toTitleCase('camelCase') // 'Camel Case'
 * toTitleCase('kebab-case-string') // 'Kebab Case String'
 */
export const toTitleCase = (str: string | undefined): string => {
  if (!str) return '';

  // Replace underscores, hyphens with spaces and split on capital letters
  const normalized = str
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();

  // Capitalize first letter of each word
  return normalized
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const sortSalaryComponents = <T extends { isPredefined?: boolean; name: string }>(
  components: T[],
): T[] =>
  components.slice().sort((current, next) => {
    if (!!current.isPredefined !== !!next.isPredefined) return current.isPredefined ? -1 : 1;
    return current.name.localeCompare(next.name);
  });

export const mapOTComponentToEarning = (
  otComponent: OTSalaryComponentListItem,
): EarningsComponentType => ({
  id: otComponent.id,
  name: otComponent.name,
  description: otComponent.description ?? undefined,
  isActive: otComponent.isActive,
  isPredefined: true,
  source: 'X_TNA',
  category: 'EARNINGS_CATEGORY',
  componentType: 'COMPONENT_TYPE_OTHER_ALLOWANCE',
  settings: {
    behaviour: {
      displayBehaviour: {
        displayName: otComponent.displayName ?? otComponent.name,
        showZero: otComponent.settings?.displayBehaviour?.showZero ?? false,
        visibleToEmployee: otComponent.settings?.displayBehaviour?.visibleToEmployee ?? true,
      },
      pfBehaviour: otComponent.settings?.pfBehaviour,
      ptBehaviour: otComponent.settings?.ptBehaviour,
      esiBehaviour: otComponent.settings?.esiBehaviour,
      lwfBehaviour: otComponent.settings?.lwfBehaviour,
      gratuityBehaviour: otComponent.settings?.gratuityBehaviour,
      leaveEncashmentBehaviour: otComponent.settings?.leaveEncashmentBehaviour,
      taxBehaviour: otComponent.settings?.taxBehaviour,
      prorationBehaviour: otComponent.settings?.prorationBehaviour,
    },
    earningDetails: { payType: 'adhoc' },
  },
});
